import { Grid } from '@material-ui/core';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../Session';
import { FullSaleTransactionDataFragment, SaleTransactionStage, UserType } from '../../../api';
import BooleanCheckbox from '../../../components/fields/BooleanCheckbox';
import OutlinedDateTimePickerField from '../../../components/fields/OutlinedDateTimePickerField';
import OutlinedInputField from '../../../components/fields/OutlinedInputField';

export type SessionDetailsFormProps = {
    saleTransaction: FullSaleTransactionDataFragment;
};

const SessionDetailsForm = ({ saleTransaction }: SessionDetailsFormProps) => {
    const { t } = useTranslation(['saleTransactionsPage', 'common']);
    const user = useCurrentUser();
    const canEditPeriod = useMemo(
        () => [UserType.Admin, UserType.Root, UserType.SalesManager, UserType.Purchaser].includes(user?.type),
        [user?.type]
    );

    const canEditEndDate = useMemo(
        () =>
            canEditPeriod &&
            [SaleTransactionStage.New, SaleTransactionStage.UpcomingBidding, SaleTransactionStage.BiddingOpen].includes(
                saleTransaction.stage
            ),
        [canEditPeriod, saleTransaction.stage]
    );

    const canEditStartDate = useMemo(
        () =>
            canEditPeriod &&
            [SaleTransactionStage.New, SaleTransactionStage.UpcomingBidding].includes(saleTransaction.stage),
        [canEditPeriod, saleTransaction.stage]
    );

    return (
        <Grid spacing={2} container>
            <Grid xs={12} item>
                <BooleanCheckbox
                    color="secondary"
                    disabled={!canEditPeriod}
                    label={t('saleTransactionsPage:sessionDetails.urgent')}
                    name="biddingSession.urgent"
                />
            </Grid>
            <Grid xs={12} item>
                <OutlinedDateTimePickerField
                    color="secondary"
                    disablePast={canEditStartDate}
                    disabled={!canEditStartDate}
                    label={t('saleTransactionsPage:sessionDetails.bidOpeningOn')}
                    name="biddingSession.startPeriod"
                    required
                />
            </Grid>
            <Grid xs={12} item>
                <OutlinedDateTimePickerField
                    color="secondary"
                    disablePast={canEditEndDate}
                    disabled={!canEditEndDate}
                    label={t('saleTransactionsPage:sessionDetails.bidClosingDate')}
                    name="biddingSession.endPeriod"
                    required
                />
            </Grid>

            <Grid xs={12} item>
                <OutlinedInputField
                    color="secondary"
                    disabled={
                        saleTransaction.stage === SaleTransactionStage.New ||
                        saleTransaction.stage === SaleTransactionStage.Closed ||
                        saleTransaction.stage === SaleTransactionStage.Cancelled
                    }
                    label={t('saleTransactionsPage:sessionDetails.bidNotes')}
                    name="biddingSession.notes"
                    placeholder={t('saleTransactionsPage:sessionDetails.bidNotes')}
                />
            </Grid>
        </Grid>
    );
};

export default SessionDetailsForm;
